<template>
  <div class="thelook-wizard-container max-w-4xl mx-auto p-4 md:p-8 relative">
    
    <!-- Locked Overlay -->
    <div v-if="wizardStore.unlockedStep < 5" class="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/40 backdrop-blur-[2px] rounded-[2.5rem]">
        <div class="text-5xl mb-4">🎛️</div>
        <h3 class="text-xl font-bold text-gray-400">Step 5: Customize & Download</h3>
        <p class="text-sm text-gray-400">Unlock Step 4 to configure your match</p>
    </div>

    <!-- Content Wrapper -->
    <div :class="{'opacity-30 grayscale pointer-events-none': wizardStore.unlockedStep < 5}">
        <div class="flex items-center justify-between mb-8">
            <h2 class="font-lds-heading text-3xl font-bold text-gray-800">Test Your Match</h2>
            <div class="flex gap-4">
                <button @click="wizardStore.resetToDefaults" class="px-6 py-2 rounded-full font-medium text-lds-primary bg-lds-primary/10 hover:bg-lds-primary/20 transition-colors">
                    Reset
                </button>
                <button @click="wizardStore.downloadSettings" class="px-6 py-2 rounded-full font-medium text-white bg-lds-primary shadow-lds-md hover:shadow-lds-lg hover:-translate-y-0.5 transition-all">
                    Download Settings
                </button>
            </div>
        </div>

        <!-- Accordion Drawers -->
        <div class="space-y-4">
            
            <!-- Drawer 1: Primary Layout & Theme -->
            <div class="drawer bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <button 
                    @click="toggleDrawer(0)"
                    class="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                    <span class="font-lds-heading font-bold text-gray-700">1. Primary Layout & Theme</span>
                    <span class="text-gray-300 transition-transform duration-300" :class="{'rotate-180': openDrawer === 0}">▼</span>
                </button>
                
                <div v-show="openDrawer === 0" class="px-6 pb-6 space-y-6 border-t border-gray-50 pt-6">
                    <!-- Expanded Theme Palette -->
                    <div class="control-group">
                        <label class="block text-sm font-medium text-gray-500 mb-4">Theme Palette</label>
                        <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <button 
                                v-for="theme in themes" 
                                :key="theme.name"
                                @click="wizardStore.applyTheme(theme.name)"
                                class="flex flex-col items-center gap-2 p-2 rounded-xl transition-all border-2"
                                :class="wizardStore.themePreset === theme.name ? 'border-lds-primary bg-lds-primary/5 scale-105' : 'border-transparent hover:bg-gray-50'"
                            >
                                <div class="w-12 h-12 rounded-lg shadow-inner ring-1 ring-black/5" :style="{ background: theme.preview }"></div>
                                <span class="text-[10px] font-bold uppercase tracking-widest text-gray-500">{{ theme.name }}</span>
                            </button>
                        </div>
                    </div>

                    <!-- Grid Layout -->
                    <div class="control-group">
                        <label class="block text-sm font-medium text-gray-500 mb-2">Grid Layout</label>
                        <div class="flex bg-gray-50 p-1 rounded-lg border border-gray-200">
                            <button 
                                @click="wizardStore.applyLayout('Square')"
                                class="flex-1 py-2 rounded-md text-sm font-medium transition-all"
                                :class="wizardStore.layout === 'Square' ? 'bg-white text-lds-primary shadow-sm' : 'text-gray-400'"
                            >
                                Square (1:1)
                            </button>
                            <button 
                                @click="wizardStore.applyLayout('Landscape')"
                                class="flex-1 py-2 rounded-md text-sm font-medium transition-all" 
                                :class="wizardStore.layout === 'Landscape' ? 'bg-white text-lds-primary shadow-sm' : 'text-gray-400'"
                            >
                                Landscape (16:9)
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Drawer 2: Grid/Border Detail -->
            <div class="drawer bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <button 
                    @click="toggleDrawer(1)"
                    class="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                    <span class="font-lds-heading font-bold text-gray-700">2. Grid/Border Detail</span>
                    <span class="text-gray-300 transition-transform duration-300" :class="{'rotate-180': openDrawer === 1}">▼</span>
                </button>

                <div v-show="openDrawer === 1" class="px-6 pb-6 space-y-6 border-t border-gray-50 pt-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <!-- Left: Spacing & Border Visibility -->
                        <div class="space-y-6">
                            <div class="control-group">
                                <div class="flex justify-between mb-2">
                                    <label class="text-sm font-medium text-gray-500">Grid Spacing</label>
                                    <span class="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{{ wizardStore.spacing }}%</span>
                                </div>
                                <input type="range" v-model.number="wizardStore.spacing" min="0" max="100" class="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-lds-primary">
                            </div>
                            <div class="control-group">
                                <label class="block text-sm font-medium text-gray-500 mb-2">Outer Border</label>
                                <div class="flex bg-gray-50 p-1 rounded-lg border border-gray-200">
                                    <button @click="wizardStore.borderVisible = true" class="flex-1 py-1 rounded text-xs" :class="wizardStore.borderVisible ? 'bg-white text-lds-primary shadow-sm' : 'text-gray-400'">Show</button>
                                    <button @click="wizardStore.borderVisible = false" class="flex-1 py-1 rounded text-xs" :class="!wizardStore.borderVisible ? 'bg-white text-red-500 shadow-sm' : 'text-gray-400'">Hide</button>
                                </div>
                            </div>
                        </div>

                        <!-- Right: Grid/Border Color -->
                        <div class="space-y-6">
                            <div class="control-group">
                                <label class="block text-sm font-medium text-gray-500 mb-2">Grid/Border Color</label>
                                <div class="flex gap-2 flex-wrap">
                                    <button v-for="color in colors" 
                                            :key="color"
                                            @click="wizardStore.borderColor = color"
                                            class="w-10 h-10 rounded-xl shadow-sm border transition-transform hover:scale-110"
                                            :class="wizardStore.borderColor === color ? 'ring-2 ring-offset-2 ring-lds-primary' : 'border-gray-200'"
                                            :style="{ backgroundColor: color }">
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Drawer 3: Center & Labels -->
            <div class="drawer bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <button 
                    @click="toggleDrawer(2)"
                    class="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                    <span class="font-lds-heading font-bold text-gray-700">3. Center & Labels</span>
                    <span class="text-gray-300 transition-transform duration-300" :class="{'rotate-180': openDrawer === 2}">▼</span>
                </button>

                <div v-show="openDrawer === 2" class="px-6 pb-6 space-y-6 border-t border-gray-50 pt-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <!-- Left Sub-Grid: Center Avatar Controls -->
                        <div class="space-y-6">
                            <div class="control-group">
                                <label class="block text-sm font-medium text-gray-500 mb-2">Center Avatar</label>
                                <div class="flex bg-gray-50 p-1 rounded-lg border border-gray-200">
                                    <button @click="wizardStore.centerVisible = true" class="flex-1 py-1 rounded text-xs" :class="wizardStore.centerVisible ? 'bg-white text-lds-primary shadow-sm' : 'text-gray-400'">Show</button>
                                    <button @click="wizardStore.centerVisible = false" class="flex-1 py-1 rounded text-xs" :class="!wizardStore.centerVisible ? 'bg-white text-red-500 shadow-sm' : 'text-gray-400'">Hide</button>
                                </div>
                            </div>
                            
                            <div class="control-group" :class="{'opacity-50 pointer-events-none': !wizardStore.centerVisible}">
                                <div class="flex justify-between mb-2">
                                    <label class="text-sm font-medium text-gray-500">Avatar Size</label>
                                    <span class="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{{ wizardStore.circleRadius }}%</span>
                                </div>
                                <input type="range" v-model.number="wizardStore.circleRadius" min="0" max="100" class="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-lds-primary">
                            </div>

                            <div class="control-group" :class="{'opacity-50 pointer-events-none': !wizardStore.centerVisible}">
                                <label class="block text-sm font-medium text-gray-500 mb-2">Avatar Ring Color</label>
                                <div class="flex gap-2 flex-wrap">
                                    <button v-for="color in colors" 
                                            :key="'center-' + color"
                                            @click="wizardStore.centerBorderColor = color"
                                            class="w-8 h-8 rounded-full shadow-sm border transition-transform hover:scale-110"
                                            :class="wizardStore.centerBorderColor === color ? 'ring-2 ring-offset-2 ring-lds-primary' : 'border-gray-200'"
                                            :style="{ backgroundColor: color }">
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Right Sub-Grid: Label Controls -->
                        <div class="space-y-6">
                            <div class="control-group">
                                <label class="block text-sm font-medium text-gray-500 mb-2">Emotion Labels</label>
                                <div class="flex bg-gray-50 p-1 rounded-lg border border-gray-200">
                                    <button @click="wizardStore.labelsVisible = true" class="flex-1 py-1 rounded text-xs" :class="wizardStore.labelsVisible ? 'bg-white text-lds-primary shadow-sm' : 'text-gray-400'">Show</button>
                                    <button @click="wizardStore.labelsVisible = false" class="flex-1 py-1 rounded text-xs" :class="!wizardStore.labelsVisible ? 'bg-white text-red-500 shadow-sm' : 'text-gray-400'">Hide</button>
                                </div>
                            </div>

                            <div class="grid grid-cols-2 gap-4" :class="{'opacity-50 pointer-events-none': !wizardStore.labelsVisible}">
                                <div class="control-subgroup">
                                    <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Label Size</label>
                                    <div class="flex bg-gray-50 p-1 rounded-lg border border-gray-200">
                                        <button @click="wizardStore.labelSize = 'S'" class="flex-1 py-1 rounded text-xs" :class="wizardStore.labelSize === 'S' ? 'bg-white text-lds-primary shadow-sm' : 'text-gray-400'">S</button>
                                        <button @click="wizardStore.labelSize = 'M'" class="flex-1 py-1 rounded text-xs" :class="wizardStore.labelSize === 'M' ? 'bg-white text-lds-primary shadow-sm' : 'text-gray-400'">M</button>
                                        <button @click="wizardStore.labelSize = 'L'" class="flex-1 py-1 rounded text-xs" :class="wizardStore.labelSize === 'L' ? 'bg-white text-lds-primary shadow-sm' : 'text-gray-400'">L</button>
                                    </div>
                                </div>
                                <div class="control-subgroup">
                                    <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Alignment</label>
                                    <div class="flex bg-gray-50 p-1 rounded-lg border border-gray-200">
                                        <button @click="wizardStore.labelAlignment = 'Outer'" class="flex-1 py-1 rounded text-xs" :class="wizardStore.labelAlignment === 'Outer' ? 'bg-white text-lds-primary shadow-sm' : 'text-gray-400'">Out</button>
                                        <button @click="wizardStore.labelAlignment = 'Inner'" class="flex-1 py-1 rounded text-xs" :class="wizardStore.labelAlignment === 'Inner' ? 'bg-white text-lds-primary shadow-sm' : 'text-gray-400'">In</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useWizardStore } from '@emoji-photo/wizard-island';

export default {
  name: 'TL_CustomizerIsland',
  setup() {
    const wizardStore = useWizardStore();
    const openDrawer = ref(0);

    const toggleDrawer = (index) => {
        openDrawer.value = openDrawer.value === index ? null : index;
    };

    const themes = [
        { name: 'Vibrant', preview: 'linear-gradient(135deg, #8B5CF6, #06B6D4)' },
        { name: 'Noir', preview: '#1A1A2E' },
        { name: 'Wildfire', preview: 'linear-gradient(135deg, #F97316, #EF4444)' },
        { name: 'Aurora', preview: 'linear-gradient(135deg, #E879F9, #6366F1)' },
        { name: 'Jungle', preview: 'linear-gradient(135deg, #10B981, #064E3B)' }
    ];

    const colors = ['#000000', '#333333', '#ffffff', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6'];

    return { wizardStore, openDrawer, toggleDrawer, themes, colors };
  }
}
</script>
